import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import { colors, fontFamily } from '../../theme';

const card = { background: colors.cardBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, padding: '1.25rem', marginBottom: '1rem', boxShadow: colors.shadowCard };
const inputStyle = { padding: '0.55rem 0.75rem', background: colors.surfaceBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 4, color: colors.textPrimary, fontFamily, fontSize: '0.85rem' };
const btnPrimary = { padding: '0.5rem 1rem', background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 4, cursor: 'pointer', fontFamily, fontSize: '0.85rem' };
const btnDanger = { padding: '0.5rem 1rem', background: 'rgba(220,53,69,0.2)', border: `1px solid ${colors.negative}`, color: colors.negative, borderRadius: 4, cursor: 'pointer', fontFamily, fontSize: '0.85rem' };

export default function GameCancellationManagement() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [voiding, setVoiding] = useState(null);
  const [result, setResult] = useState(null);
  const [reason, setReason] = useState('Game cancelled/postponed');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const data = await api.admin.getPendingBetsByGame();
      setGames(data.games || []);
    } catch (e) {
      console.error('Failed to load games', e);
    } finally {
      setLoading(false);
    }
  };

  const handleVoid = async (game) => {
    if (!window.confirm(
      `Void ALL pending bets for game "${game.game_id}"?\n\n` +
      `${game.single_count} single bet(s) + ${game.parlay_leg_count} parlay leg(s)\n\n` +
      `This will refund all affected customers. This cannot be undone.`
    )) return;

    setVoiding(game.game_id);
    setResult(null);
    try {
      const res = await api.admin.voidGame(game.game_id, game.sport_key, reason);
      setResult({ success: true, ...res });
      await load();
    } catch (e) {
      setResult({ success: false, error: e.response?.data?.error || 'Failed to void game' });
    } finally {
      setVoiding(null);
    }
  };

  const filtered = games.filter(g =>
    !search ||
    g.game_id.toLowerCase().includes(search.toLowerCase()) ||
    (g.description || '').toLowerCase().includes(search.toLowerCase()) ||
    (g.sport_key || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <h2 style={{ color: colors.textPrimary, margin: '0 0 0.5rem 0', fontSize: '1.1rem', fontWeight: 700 }}>Game Cancellation</h2>
      <p style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '1.5rem' }}>
        Void all pending bets on a specific game. Single bets are cancelled (credit restored); parlay legs are removed and payouts are recalculated for remaining legs.
      </p>

      {result && (
        <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', borderRadius: 6, background: result.success ? 'rgba(40,167,69,0.15)' : 'rgba(220,53,69,0.15)', border: `1px solid ${result.success ? colors.odds : colors.negative}`, color: result.success ? colors.odds : colors.negative, fontSize: '0.85rem' }}>
          {result.success
            ? `Done — ${result.single_cancelled} single bet(s) cancelled, ${result.parlay_legs_voided} parlay leg(s) voided, ${result.parlays_resolved} parlay(s) resolved.`
            : result.error}
        </div>
      )}

      <div style={{ ...card, display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by game ID, team, or sport…"
          style={{ ...inputStyle, flex: '1', minWidth: 200 }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ color: colors.textMuted, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>Reason:</label>
          <input
            value={reason}
            onChange={e => setReason(e.target.value)}
            style={{ ...inputStyle, minWidth: 220 }}
          />
        </div>
        <button onClick={load} style={btnPrimary}>Refresh</button>
      </div>

      {loading ? (
        <div style={{ color: colors.textMuted, textAlign: 'center', padding: '3rem' }}>Loading pending games…</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', color: colors.textMuted, fontSize: '0.9rem' }}>
          {games.length === 0 ? 'No pending bets found.' : 'No games match your search.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {filtered.map(game => (
            <div key={game.game_id} style={{ ...card, display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: 0 }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <p style={{ color: colors.textPrimary, fontWeight: 600, margin: '0 0 0.2rem 0', fontSize: '0.9rem', wordBreak: 'break-all' }}>{game.game_id}</p>
                <p style={{ color: colors.textMuted, margin: 0, fontSize: '0.8rem' }}>{game.description || '—'}</p>
                <p style={{ color: colors.textMuted, margin: '0.15rem 0 0 0', fontSize: '0.75rem' }}>{game.sport_key}</p>
              </div>
              <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center', fontSize: '0.8rem' }}>
                <span style={{ color: colors.textMuted }}>
                  <strong style={{ color: colors.textPrimary }}>{game.single_count}</strong> single{game.single_count !== 1 ? 's' : ''}
                </span>
                <span style={{ color: colors.textMuted }}>
                  <strong style={{ color: colors.textPrimary }}>{game.parlay_leg_count}</strong> parlay leg{game.parlay_leg_count !== 1 ? 's' : ''}
                </span>
                {game.total_amount > 0 && (
                  <span style={{ color: colors.textMuted }}>
                    <strong style={{ color: colors.pending }}>${game.total_amount.toFixed(2)}</strong> at stake
                  </span>
                )}
              </div>
              <button
                onClick={() => handleVoid(game)}
                disabled={voiding === game.game_id}
                style={{ ...btnDanger, opacity: voiding === game.game_id ? 0.6 : 1 }}
              >
                {voiding === game.game_id ? 'Voiding…' : 'Void All Bets'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
